import { ButtonView, GridView } from '@vivli/shared/views';
import React, { CSSProperties } from 'react';
import { ColDef } from 'ag-grid-community';
import { Color, Size } from '@vivli/shared/theme';
import { DisclosureStatusEnum, DisclosureTypeEnum } from '@vivli/features/disclosures/infrastructure/enum';
import { GridCellRendererEnum } from '@vivli/shared/infrastructure/enum';
import { IDisclosure } from '@vivli/features/disclosures/infrastructure/interface';
import { useDisclosureStatus } from '@vivli/features/disclosures/infrastructure/hook';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';

const containerStyle: CSSProperties = {
    padding: `0 ${Size.INSET_SIZE}px`,
    height: '100%',
    position: 'relative',
    backgroundColor: 'rgb(233, 234, 239)',
};

const btnContainerStyle: CSSProperties = {
    padding: '40px',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    columnGap: '2em',
};

const btnStyle: CSSProperties = {
    width: '200px',
};
const deleteBtnStyle: CSSProperties = {
    padding: 0,
    minWidth: '45px',
    width: '45px',
};
const customContainerStyle: CSSProperties = {
    display: 'grid',
    placeContent: 'center end',
};

interface DisclosuresViewProps {
    disclosures: any[];
    showFilters?: boolean;
    canAddDisclosure?: boolean;
    onRowClick?: (disclosure: IDisclosure) => void;
    onAddDisclosureClick?: () => void;
    isCreating?: boolean;
    canDeleteDisclosure?: boolean;
    onDelete?: (disclosure: IDisclosure) => void;
}

export const DisclosuresView = ({
    disclosures,
    showFilters,
    onRowClick,
    onAddDisclosureClick,
    canAddDisclosure,
    isCreating,
    canDeleteDisclosure,
    onDelete,
}: DisclosuresViewProps) => {
    const getDisclosureStatus = useDisclosureStatus();

    const RowControlsView = (disclosure: IDisclosure) => {
        return (
            <div>
                <ButtonView title={'Delete Disclosure'} style={deleteBtnStyle} onClick={() => onDelete(disclosure)} theme={'red-on-white'}>
                    <FontAwesomeIcon style={{ cursor: 'pointer', color: Color.ERROR_RED }} icon={faTrash} />
                </ButtonView>
            </div>
        );
    };

    const gridCols: ColDef[] = [
        {
            headerName: 'Disclosure ID',
            field: 'vivliDisclosureId',
            maxWidth: 150,
            sort: 'desc',
        },
        {
            headerName: 'Title',
            field: 'title',
            wrapText: true,
            cellRenderer: GridCellRendererEnum.GridCellLongText,
        },
        {
            headerName: 'Type',
            field: 'disclosureType',
            valueGetter: ({ data }) => DisclosureTypeEnum[data.disclosureType],
            maxWidth: 150,
        },
        {
            headerName: 'Data Contributor(s)',
            field: 'contributingOrgDetails',
            cellRenderer: GridCellRendererEnum.GridCellList,
            valueGetter: ({ data }) => {
                const dataContributors = data?.contributingOrgDetails?.map((d) => d.organizationName);
                return dataContributors?.length > 0 ? dataContributors : ['N/A'];
            },
        },
        {
            headerName: 'Status',
            field: 'status',
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            cellRendererParams: ({ data }) => ({
                titleText: getDisclosureStatus(data),
            }),
            valueFormatter: ({ value }) => {
                switch (value as DisclosureStatusEnum) {
                    case DisclosureStatusEnum.Draft:
                        return 'Draft';
                    case DisclosureStatusEnum.Published:
                        return 'Published';
                    case DisclosureStatusEnum.NotPublished:
                        return 'Not Published';
                    case DisclosureStatusEnum.ReviewComplete:
                        return 'Review Complete';
                    case DisclosureStatusEnum.SubmittedToPublisher:
                        return 'Submitted To Publisher';
                    case DisclosureStatusEnum.ThirtyDayReview:
                        return '30-Day Review';
                    case DisclosureStatusEnum.Withdrawn:
                        return 'Withdrawn';
                }
            },
            maxWidth: 150,
        },
        {
            headerName: '',
            hide: !canDeleteDisclosure || !onDelete,
            cellRenderer: GridCellRendererEnum.GridCustomDisplay,
            cellRendererParams: ({ data }) => {
                return {
                    containerStyle: customContainerStyle,
                    component: RowControlsView(data),
                };
            },
            maxWidth: 80,
            minWidth: 80,
            suppressMovable: true,
            sortable: false,
            filter: false,
            floatingFilter: false,
        },
    ];

    const grid = (
        <GridView
            rowData={disclosures}
            columns={gridCols}
            hideHeader={false}
            hideFloatingFilter={!showFilters}
            fullWidthColumns={true}
            onRowClick={onRowClick}
            emptyMessage={'No disclosures found.'}
            autoHeight={true}
            rowHeight={60}
            pagination={true}
            pageSize={20}
        />
    );

    return (
        <div style={containerStyle}>
            <div style={btnContainerStyle}>
                <div>
                    As per the Data Use Agreement (DUA), Researcher(s) should send any public disclosures (manuscripts, abstracts, posters,
                    slides, etc.) to Data Contributor(s) via this form at least 30 days prior to submission of materials to a learned forum
                    or journal. As per the DUA, during the 30-day review period, Data Contributor(s) may provide non-binding comments
                    regarding the scientific content. They may also possibly request the deletion of any confidential information (as
                    defined in the DUA).
                </div>
                {canAddDisclosure && (
                    <ButtonView disabled={!disclosures || isCreating} style={btnStyle} onClick={onAddDisclosureClick} dataId={'add-disclosure-btn'}>
                        Add Disclosure
                    </ButtonView>
                )}
            </div>
            <div>{grid}</div>
        </div>
    );
};
